import { keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import { KeenSliderInstance } from 'keen-slider'
import { useKeenSlider } from 'keen-slider/react'
import Image from 'next/image'
import React, {
    type CSSProperties,
    useCallback,
    useEffect,
    useMemo,
    useReducer,
    useRef,
    useState,
} from 'react'
import { createPortal } from 'react-dom'
import { InspectOverlayHost } from '../../shared/InspectOverlayHost'
import {
    PRIMARY_PAGE_FILL_COLOR_VAR,
    PRIMARY_PAGE_TEXT_COLOR_VAR,
} from '../../../context/CustomizationContextProvider'
import { CarouselChevron } from '../../icons/Icons'
import { useIsomorphicLayoutEffect } from '../../../hooks/useIsomorphicLayoutEffect'
import { PRODUCT_IMAGE_CAROUSEL_MAIN_KEEN_CLASS } from '../../../style/keenStyles'
import { getTypographyRoleStyle } from '../../../style/typography'
import { belowFoldImageProps } from '../../../utils/getFirstLcpImageSectionIndex'
import {
    imageUrlTransform,
    imageLqipPreviewUrl,
    supportsImageLqipPreview,
} from '../../../utils/imageUrlTransform'
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock'
import {
    inScrollingDesktop,
    scaledFontSize,
} from '../../../style/scrollingLayout'

type CarouselState = {
    currentSlide: number
    loadedImages: Set<number>
    isFullscreen: boolean
}

type CarouselAction =
    | { type: 'SET_SLIDE'; payload: number }
    | { type: 'TOGGLE_FULLSCREEN'; payload: boolean }
    | { type: 'MARK_IMAGE_LOADED'; payload: number }

function carouselReducer(
    state: CarouselState,
    action: CarouselAction,
): CarouselState {
    switch (action.type) {
        case 'SET_SLIDE':
            return {
                ...state,
                currentSlide: action.payload,
            }
        case 'TOGGLE_FULLSCREEN':
            return {
                ...state,
                isFullscreen: action.payload,
            }
        case 'MARK_IMAGE_LOADED':
            return {
                ...state,
                loadedImages: new Set([...state.loadedImages, action.payload]),
            }
        default:
            return state
    }
}

const CAROUSEL_HEIGHT = 300

/** Set on an ancestor to raise gallery height without threading a prop. */
export const PDP_GALLERY_HEIGHT_VAR = '--sf-pdp-gallery-height'
const GALLERY_HEIGHT = `var(${PDP_GALLERY_HEIGHT_VAR}, ${CAROUSEL_HEIGHT}px)`

const CAROUSEL_CONFIG = {
    SLIDE_SPACING: 8,
    DRAG_SPEED: 0.8,
    OFF_WINDOW_PLACEHOLDER_WIDTH: 120,
    VISIBLE_PORTION_THRESHOLD: 0,
} as const

const MAIN_RAIL_IMAGE_LAYOUT_STYLE: CSSProperties = {
    height: '100%',
    width: 'auto',
    maxWidth: '100%',
    objectFit: 'contain',
    display: 'block',
}

function setsEqual(a: Set<number>, b: Set<number>): boolean {
    if (a.size !== b.size) return false
    for (const value of a) {
        if (!b.has(value)) return false
    }
    return true
}

function collectMountedSlideIndices(
    slider: KeenSliderInstance,
    slideCount: number,
    {
        prioritizeHero,
        heroReady,
    }: { prioritizeHero: boolean; heroReady: boolean },
): Set<number> {
    if (slideCount <= 1) return new Set([0])

    const details = slider.track.details
    if (!details) return new Set([0])

    const indices = new Set<number>()
    const rel = details.rel
    const blockPrefetchNeighbors = prioritizeHero && rel === 0 && !heroReady

    for (const slide of details.slides) {
        if (slide.portion <= CAROUSEL_CONFIG.VISIBLE_PORTION_THRESHOLD) continue
        const abs = slider.track.absToRel(slide.abs)
        if (blockPrefetchNeighbors && abs !== 0) continue
        indices.add(abs)
    }

    for (const offset of [-1, 1] as const) {
        const idx = (rel + offset + slideCount) % slideCount
        if (blockPrefetchNeighbors && idx !== 0) continue
        indices.add(idx)
    }

    indices.add(rel)
    return indices
}

const CAROUSEL_IMAGE_BLUR_SCALE_TRANSITION =
    'filter 0.4s ease, transform 0.4s ease'

const CAROUSEL_IMAGE_MAIN_FS_TRANSITION = `${CAROUSEL_IMAGE_BLUR_SCALE_TRANSITION}, opacity 0.3s ease-in-out`

function carouselImageRevealStyle({
    loaded,
    variant,
}: {
    loaded: boolean
    variant: 'mainRail' | 'fullscreen'
}): Pick<CSSProperties, 'filter' | 'transform' | 'transition'> {
    const transition =
        variant === 'fullscreen'
            ? CAROUSEL_IMAGE_MAIN_FS_TRANSITION
            : CAROUSEL_IMAGE_BLUR_SCALE_TRANSITION

    if (variant === 'fullscreen') {
        return {
            filter: loaded ? 'blur(0)' : 'blur(4px)',
            transform: loaded
                ? 'translate(-50%, -50%) scale(1)'
                : 'translate(-50%, -50%) scale(1.03)',
            transition,
        }
    }
    return {
        filter: loaded ? 'blur(0)' : 'blur(4px)',
        transform: loaded ? 'scale(1)' : 'scale(1.03)',
        transition,
    }
}

function mainRailImageRevealStyle(
    imageLoaded: boolean,
    isActiveSlide: boolean,
): Pick<CSSProperties, 'filter' | 'transform' | 'transition'> {
    if (!imageLoaded && isActiveSlide) {
        return carouselImageRevealStyle({
            loaded: imageLoaded,
            variant: 'mainRail',
        })
    }
    return { transition: 'opacity 0.3s ease-in-out' }
}

type CarouselMetrics = { height: number; width: number }

/**
 * A landscape image renders shorter than the gallery's nominal height, and the
 * slack under it reads as a gap before the options. Giving the box the hero
 * image's own ratio lets it end where the image does, with the nominal height
 * still the ceiling and the fallback until the ratio is known.
 *
 * The width has to be stated: left to `auto` the ceiling transfers through the
 * ratio into a maximum width, and the gallery stops short of its column on any
 * screen wider than the ceiling is tall.
 */
function galleryBoxHeight(
    maxHeight: string,
    heroRatio: number | undefined,
): CSSProperties {
    if (!heroRatio) return { height: maxHeight, minHeight: maxHeight }
    return {
        width: '100%',
        aspectRatio: String(heroRatio),
        maxHeight,
        height: 'auto',
    }
}

// Explicit, stable pixel width for a slide from its image aspect ratio and the
// measured carousel box. Giving keen exact integer widths (instead of letting
// it re-measure `auto`-sized slides) avoids its loop/rounding re-snap bugs with
// variable-width slides, and the width never changes once the ratio is known,
// so a lazily-loaded image can't shift geometry underneath the track.
// Returns undefined before the box is measured; the caller falls back.
function slideWidthPx(
    ratio: number | undefined,
    metrics: CarouselMetrics | null,
): number | undefined {
    if (!metrics || metrics.height <= 0 || metrics.width <= 0) return undefined
    // Unknown ratio (still loading or errored) reserves a square so the slide
    // can't collapse; a wide image is clamped to the container (letterboxed).
    const natural = ratio != null ? metrics.height * ratio : metrics.height
    return Math.min(Math.round(natural), Math.round(metrics.width))
}

function slideSizingStyle(
    width: number | undefined,
): CSSProperties | undefined {
    if (width == null) return undefined
    return { flex: `0 0 ${width}px`, width, minWidth: width, maxWidth: width }
}

function placeholderSlideStyle(hasExplicitSlideWidth: boolean): CSSProperties {
    return {
        ...(hasExplicitSlideWidth
            ? { width: '100%' }
            : {
                  width: CAROUSEL_CONFIG.OFF_WINDOW_PLACEHOLDER_WIDTH,
                  minWidth: CAROUSEL_CONFIG.OFF_WINDOW_PLACEHOLDER_WIDTH,
              }),
        height: '100%',
        backgroundColor: `color-mix(in srgb, var(${PRIMARY_PAGE_TEXT_COLOR_VAR}) 6%, transparent 94%)`,
    }
}

interface ProductImageCarouselProps {
    imageUrls: string[]
    onSlideChange?: () => void
    maxImageHeight?: string
    /**
     * When true, slide 0 uses Next/Image `priority` (built-in preload + eager load).
     * Disable for inline PDP below the fold so other prioritized images win.
     */
    prioritizeHero?: boolean
}

/**
 * The zoomed viewer, shared by every gallery layout. It owns the slide it is
 * showing and reports moves back so the gallery behind it can follow along.
 */
const FullscreenGallery: React.FC<{
    imageUrls: string[]
    initialIndex: number
    onClose: () => void
    onIndexChange?: (index: number) => void
}> = ({ imageUrls, initialIndex, onClose, onIndexChange }) => {
    const [currentSlide, setCurrentSlide] = useState(initialIndex)
    const [loaded, setLoaded] = useState<Set<number>>(() => new Set())
    const onIndexChangeRef = useRef(onIndexChange)
    onIndexChangeRef.current = onIndexChange

    useBodyScrollLock(true)

    const [sliderRef] = useKeenSlider<HTMLDivElement>({
        initial: initialIndex,
        slides: { perView: 1 },
        loop: true,
        drag: true,
        slideChanged(slider) {
            const index = slider.track.details.rel
            setCurrentSlide(index)
            onIndexChangeRef.current?.(index)
        },
    })

    const markLoaded = useCallback((idx: number) => {
        setLoaded((prev) => (prev.has(idx) ? prev : new Set([...prev, idx])))
    }, [])

    return (
        <Portal>
            <ModalOverlay onClick={onClose} />
            <ModalContent>
                <CloseButton
                    onClick={(e) => {
                        e.stopPropagation()
                        onClose()
                    }}
                >
                    &times;
                </CloseButton>
                <div ref={sliderRef} className="keen-slider">
                    {imageUrls.map((imageUrl, idx) => (
                        <FullscreenSlide
                            className="keen-slider__slide"
                            key={idx}
                        >
                            <Image
                                src={imageUrlTransform(imageUrl, 'main')}
                                style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    maxWidth: '90vw',
                                    maxHeight: '90vh',
                                    width: 'auto',
                                    height: 'auto',
                                    objectFit: 'contain',
                                    opacity: loaded.has(idx) ? 1 : 0,
                                    ...carouselImageRevealStyle({
                                        loaded: loaded.has(idx),
                                        variant: 'fullscreen',
                                    }),
                                }}
                                width={1200}
                                height={1200}
                                alt={`Product image ${idx + 1}`}
                                unoptimized
                                priority={idx === initialIndex}
                                onLoadingComplete={() => markLoaded(idx)}
                                onError={() => markLoaded(idx)}
                            />
                        </FullscreenSlide>
                    ))}
                </div>
                <ImageCounter>
                    {currentSlide + 1} / {imageUrls.length}
                </ImageCounter>
            </ModalContent>
        </Portal>
    )
}

const SingleImageDisplay: React.FC<{
    imageUrl: string
    maxImageHeight?: string
    prioritizeHero: boolean
}> = ({ imageUrl, maxImageHeight, prioritizeHero }) => {
    const effectiveMaxHeight = maxImageHeight ?? GALLERY_HEIGHT
    const [loaded, setLoaded] = useState(false)
    const [ratio, setRatio] = useState<number | undefined>(undefined)
    const [isFullscreen, setIsFullscreen] = useState(false)
    const hasLqip = supportsImageLqipPreview(imageUrl)
    const lqipUrl = hasLqip ? imageLqipPreviewUrl(imageUrl) : imageUrl

    const openFullscreen = () => setIsFullscreen(true)
    const closeFullscreen = () => setIsFullscreen(false)
    // The LQIP carries the same ratio and lands first, so the box settles on
    // its final height before the full image arrives.
    const recordRatio = (img: HTMLImageElement) => {
        if (!img.naturalWidth || !img.naturalHeight) return
        setRatio((prev) => prev ?? img.naturalWidth / img.naturalHeight)
    }

    return (
        <div
            style={{
                width: '100%',
                backgroundColor: `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`,
                boxSizing: 'border-box',
                position: 'relative',
                zIndex: 0,
            }}
        >
            <div
                style={{
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    ...galleryBoxHeight(effectiveMaxHeight, ratio),
                }}
            >
                {hasLqip && !loaded && (
                    <Image
                        src={lqipUrl}
                        style={MAIN_RAIL_IMAGE_LAYOUT_STYLE}
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 800px, 1200px"
                        width={1200}
                        height={1200}
                        unoptimized
                        alt=""
                        aria-hidden
                        {...belowFoldImageProps(!prioritizeHero)}
                        onLoad={(e) => recordRatio(e.currentTarget)}
                    />
                )}
                {!loaded && !hasLqip && <MainSlideShimmer aria-hidden />}
                <Image
                    src={imageUrlTransform(imageUrl, 'main')}
                    style={{
                        ...MAIN_RAIL_IMAGE_LAYOUT_STYLE,
                        opacity: loaded ? 1 : 0,
                        cursor: 'zoom-in',
                        position: loaded ? 'relative' : 'absolute',
                        top: loaded ? undefined : 0,
                        left: loaded ? undefined : 0,
                        ...carouselImageRevealStyle({
                            loaded,
                            variant: 'mainRail',
                        }),
                    }}
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 800px, 1200px"
                    width={1200}
                    height={1200}
                    priority={prioritizeHero}
                    fetchPriority={prioritizeHero ? 'high' : 'low'}
                    loading={prioritizeHero ? 'eager' : 'lazy'}
                    unoptimized
                    alt="Product image"
                    onClick={openFullscreen}
                    onLoadingComplete={(img) => {
                        recordRatio(img)
                        setLoaded(true)
                    }}
                    onError={() => setLoaded(true)}
                />
            </div>

            {isFullscreen && (
                <FullscreenGallery
                    imageUrls={[imageUrl]}
                    initialIndex={0}
                    onClose={closeFullscreen}
                />
            )}
        </div>
    )
}

export const ProductImageCarousel: React.FC<ProductImageCarouselProps> = ({
    imageUrls,
    onSlideChange,
    maxImageHeight,
    prioritizeHero = true,
}) => {
    if (imageUrls.length <= 1) {
        return (
            <SingleImageDisplay
                imageUrl={imageUrls[0] ?? ''}
                maxImageHeight={maxImageHeight}
                prioritizeHero={prioritizeHero}
            />
        )
    }

    // A different gallery remounts the carousel, so slide caches, measurements
    // and the slider instance start fresh instead of being reset in an effect.
    // (A remount, unlike an effect, can't clobber a ratio a cached slide
    // already recorded in its onLoad on first mount.)
    return (
        <MultiImageCarousel
            key={imageUrls.join('\n')}
            imageUrls={imageUrls}
            onSlideChange={onSlideChange}
            maxImageHeight={maxImageHeight}
            prioritizeHero={prioritizeHero}
        />
    )
}

const MultiImageCarousel: React.FC<ProductImageCarouselProps> = ({
    imageUrls,
    onSlideChange,
    maxImageHeight,
    prioritizeHero = true,
}) => {
    const [state, dispatch] = useReducer(carouselReducer, {
        currentSlide: 0,
        loadedImages: new Set<number>(),
        isFullscreen: false,
    })

    const effectiveMaxHeight = maxImageHeight ?? GALLERY_HEIGHT

    const heroHasLqip =
        imageUrls.length > 0 && supportsImageLqipPreview(imageUrls[0])

    const { currentSlide, loadedImages, isFullscreen } = state

    const [slideRatios, setSlideRatios] = useState<Record<number, number>>({})
    const [metrics, setMetrics] = useState<CarouselMetrics | null>(null)
    const [mountedSlideIndices, setMountedSlideIndices] = useState<Set<number>>(
        () => new Set([0]),
    )
    const slideRatiosRef = useRef<Record<number, number>>({})
    const isDraggingRef = useRef(false)
    const pendingSliderUpdateRef = useRef(false)
    const sliderInstanceRef = useRef<KeenSliderInstance | null>(null)
    const prioritizeHeroRef = useRef(false)
    const loadedImagesRef = useRef<Set<number>>(new Set())
    const imageUrlsLengthRef = useRef(0)

    prioritizeHeroRef.current = prioritizeHero
    loadedImagesRef.current = loadedImages
    imageUrlsLengthRef.current = imageUrls.length

    const syncMountedSlideIndices = (slider: KeenSliderInstance) => {
        const next = collectMountedSlideIndices(
            slider,
            imageUrlsLengthRef.current,
            {
                prioritizeHero: prioritizeHeroRef.current,
                heroReady: loadedImagesRef.current.has(0),
            },
        )
        setMountedSlideIndices((prev) => (setsEqual(prev, next) ? prev : next))
    }

    const syncMountedSlideIndicesRef = useRef(syncMountedSlideIndices)
    syncMountedSlideIndicesRef.current = syncMountedSlideIndices

    const flushPendingSliderUpdate = () => {
        if (!pendingSliderUpdateRef.current) return
        const slider = sliderInstanceRef.current
        if (!slider || slider.animator.active || isDraggingRef.current) return
        pendingSliderUpdateRef.current = false
        slider.update()
    }

    const requestSliderUpdate = () => {
        const slider = sliderInstanceRef.current
        if (!slider) return

        if (slider.animator.active || isDraggingRef.current) {
            pendingSliderUpdateRef.current = true
            return
        }

        pendingSliderUpdateRef.current = false
        slider.update()
    }

    const requestSliderUpdateRef = useRef(requestSliderUpdate)
    requestSliderUpdateRef.current = requestSliderUpdate

    const recordSlideRatioRef = useRef<
        (idx: number, naturalWidth: number, naturalHeight: number) => void
    >(() => {})

    // Record an image's aspect ratio (from the tiny LQIP already in flight, or
    // the main image); `slideWidthPx` turns it into an explicit slide width so
    // keen snaps to the correct offset before navigation. Set-once: the LQIP
    // estimate is never overwritten, so a slide's width never changes after it
    // loads and no corrective post-navigation `update()` can snap the track. A
    // layout effect re-measures keen after the derived width commits (not here).
    const recordSlideRatio = (
        idx: number,
        naturalWidth: number,
        naturalHeight: number,
    ) => {
        if (!naturalWidth || !naturalHeight) return
        if (slideRatiosRef.current[idx] != null) return

        const ratio = naturalWidth / naturalHeight
        slideRatiosRef.current = { ...slideRatiosRef.current, [idx]: ratio }
        setSlideRatios((prev) => ({ ...prev, [idx]: ratio }))
    }

    recordSlideRatioRef.current = recordSlideRatio

    const handleImageClick = useCallback((idx: number) => {
        sliderInstanceRef.current?.moveToIdx(idx, false, { duration: 0 })
        dispatch({ type: 'TOGGLE_FULLSCREEN', payload: true })
        dispatch({ type: 'SET_SLIDE', payload: idx })
    }, [])

    const closeFullscreen = useCallback(() => {
        dispatch({ type: 'TOGGLE_FULLSCREEN', payload: false })
    }, [])

    // Keep the rail on whatever the zoomed viewer moved to, so closing it
    // leaves the shopper on the image they were looking at.
    const followFullscreenSlide = useCallback((idx: number) => {
        dispatch({ type: 'SET_SLIDE', payload: idx })
        sliderInstanceRef.current?.moveToIdx(idx, false, { duration: 0 })
    }, [])

    const mainSliderConfig = useMemo(
        () => ({
            initial: 0,
            slides: {
                perView: 'auto' as const,
                spacing: CAROUSEL_CONFIG.SLIDE_SPACING,
            },
            loop: true,
            renderMode: 'performance' as const,
            drag: true,
            dragSpeed: CAROUSEL_CONFIG.DRAG_SPEED,
            created(slider: KeenSliderInstance) {
                syncMountedSlideIndicesRef.current(slider)
            },
            updated(slider: KeenSliderInstance) {
                if (isDraggingRef.current) return
                syncMountedSlideIndicesRef.current(slider)
            },
            detailsChanged(slider: KeenSliderInstance) {
                if (isDraggingRef.current) return
                syncMountedSlideIndicesRef.current(slider)
            },
            dragStarted() {
                isDraggingRef.current = true
            },
            dragEnded(slider: KeenSliderInstance) {
                isDraggingRef.current = false
                syncMountedSlideIndicesRef.current(slider)
                flushPendingSliderUpdate()
            },
            animationEnded(slider: KeenSliderInstance) {
                syncMountedSlideIndicesRef.current(slider)
                flushPendingSliderUpdate()
            },
            slideChanged(slider: KeenSliderInstance) {
                const currentIdx = slider.track.details.rel
                dispatch({ type: 'SET_SLIDE', payload: currentIdx })
                if (!isDraggingRef.current) {
                    syncMountedSlideIndicesRef.current(slider)
                }

                onSlideChange?.()
            },
        }),

        [onSlideChange],
    )

    const [sliderRef, instanceRef] =
        useKeenSlider<HTMLDivElement>(mainSliderConfig)

    sliderInstanceRef.current = instanceRef.current

    const handleMainImageLoad = useCallback(
        (idx: number, img: HTMLImageElement) => {
            dispatch({ type: 'MARK_IMAGE_LOADED', payload: idx })
            loadedImagesRef.current = new Set([...loadedImagesRef.current, idx])
            if (idx === 0 && sliderInstanceRef.current) {
                syncMountedSlideIndicesRef.current(sliderInstanceRef.current)
            }
            recordSlideRatioRef.current(
                idx,
                img.naturalWidth,
                img.naturalHeight,
            )
        },
        [],
    )

    const handleMainImageError = useCallback((idx: number) => {
        dispatch({ type: 'MARK_IMAGE_LOADED', payload: idx })
        loadedImagesRef.current = new Set([...loadedImagesRef.current, idx])
        if (idx === 0 && sliderInstanceRef.current) {
            syncMountedSlideIndicesRef.current(sliderInstanceRef.current)
        }
    }, [])

    const handlePrev = useCallback(() => {
        instanceRef.current?.prev()
    }, [instanceRef])

    const handleNext = useCallback(() => {
        instanceRef.current?.next()
    }, [instanceRef])

    const mainSlides = useMemo(
        () =>
            imageUrls.map((imageUrl, idx) => {
                const renderImages = mountedSlideIndices.has(idx)
                const isActiveSlide = idx === currentSlide
                const hasLqip = supportsImageLqipPreview(imageUrl)
                const lqipUrl = hasLqip
                    ? imageLqipPreviewUrl(imageUrl)
                    : imageUrl
                const imageLoaded = loadedImages.has(idx)
                const ratio = slideRatios[idx]
                const slideWidth = slideWidthPx(ratio, metrics)
                return (
                    <ProductImageSlideEmotion
                        className="keen-slider__slide"
                        key={idx}
                        style={{
                            height: '100%',
                            ...slideSizingStyle(slideWidth),
                        }}
                    >
                        {renderImages ? (
                            <SlideMediaFrame>
                                {hasLqip && !imageLoaded && (
                                    <Image
                                        src={lqipUrl}
                                        style={MAIN_RAIL_IMAGE_LAYOUT_STYLE}
                                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 800px, 1200px"
                                        width={1200}
                                        height={1200}
                                        unoptimized
                                        alt=""
                                        aria-hidden
                                        {...belowFoldImageProps(
                                            !(prioritizeHero && idx === 0),
                                        )}
                                        onLoad={(e) =>
                                            recordSlideRatioRef.current(
                                                idx,
                                                e.currentTarget.naturalWidth,
                                                e.currentTarget.naturalHeight,
                                            )
                                        }
                                    />
                                )}
                                {!imageLoaded && !hasLqip && (
                                    <MainSlideShimmer aria-hidden />
                                )}
                                <Image
                                    src={imageUrlTransform(imageUrl, 'main')}
                                    style={{
                                        ...MAIN_RAIL_IMAGE_LAYOUT_STYLE,
                                        opacity: imageLoaded ? 1 : 0,
                                        cursor: 'zoom-in',
                                        position: imageLoaded
                                            ? 'relative'
                                            : 'absolute',
                                        top: imageLoaded ? undefined : 0,
                                        left: imageLoaded ? undefined : 0,
                                        ...mainRailImageRevealStyle(
                                            imageLoaded,
                                            isActiveSlide,
                                        ),
                                    }}
                                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 800px, 1200px"
                                    width={1200}
                                    height={1200}
                                    priority={prioritizeHero && idx === 0}
                                    fetchPriority={
                                        prioritizeHero && idx === 0
                                            ? 'high'
                                            : 'low'
                                    }
                                    loading={
                                        prioritizeHero && idx === 0
                                            ? 'eager'
                                            : 'lazy'
                                    }
                                    unoptimized
                                    alt={`Product image ${idx + 1}`}
                                    onClick={() => handleImageClick(idx)}
                                    onLoad={(e) =>
                                        handleMainImageLoad(
                                            idx,
                                            e.currentTarget,
                                        )
                                    }
                                    onError={() => handleMainImageError(idx)}
                                />
                            </SlideMediaFrame>
                        ) : (
                            <div
                                aria-hidden="true"
                                style={placeholderSlideStyle(
                                    slideWidth != null,
                                )}
                            />
                        )}
                    </ProductImageSlideEmotion>
                )
            }),
        [
            imageUrls,
            loadedImages,
            slideRatios,
            metrics,
            mountedSlideIndices,
            currentSlide,
            handleImageClick,
            handleMainImageLoad,
            handleMainImageError,
            prioritizeHero,
        ],
    )

    useIsomorphicLayoutEffect(() => {
        if (!instanceRef.current) return
        syncMountedSlideIndicesRef.current(instanceRef.current)
    }, [prioritizeHero, instanceRef])

    // Re-measure keen after the DOM that determines slide widths commits: a
    // newly learned aspect ratio, a new box measurement, or a change to which
    // slides are mounted. Running after commit (before paint) means keen always
    // reads the current explicit slide widths and never snaps stale geometry.
    useIsomorphicLayoutEffect(() => {
        requestSliderUpdateRef.current()
    }, [slideRatios, metrics, mountedSlideIndices])

    // Learn slide aspect ratios ahead of time from their tiny LQIP previews (the
    // images the carousel loads anyway). keen's loop geometry depends on all
    // slide widths, so locking them up front stops the wrap boundary from
    // overshooting on partial knowledge. Preloading via the Image browser API is
    // a genuine external side effect.
    useEffect(() => {
        let cancelled = false
        for (let idx = 0; idx < imageUrls.length; idx++) {
            const url = imageUrls[idx]
            // Skip slides already measured, and hosts without a tiny preview
            // rendition — eagerly fetching full images for the whole gallery
            // would be a heavy download, so those record their ratio when they
            // mount and their main image loads instead.
            if (
                slideRatiosRef.current[idx] != null ||
                !supportsImageLqipPreview(url)
            ) {
                continue
            }

            const preloadIdx = idx
            const img = new window.Image()
            // `cancelled` ignores callbacks from a superseded run/gallery so a
            // stale preview can't set-once-lock a wrong ratio.
            img.onload = () => {
                if (cancelled) return
                recordSlideRatioRef.current(
                    preloadIdx,
                    img.naturalWidth,
                    img.naturalHeight,
                )
            }
            img.src = imageLqipPreviewUrl(url)
        }
        return () => {
            cancelled = true
        }
    }, [imageUrls])

    // Measure the carousel box so slide widths can be computed in exact pixels.
    // ResizeObserver keeps it current; widths recompute from the cached ratios
    // (no image refetch) on resize.
    useIsomorphicLayoutEffect(() => {
        const container = instanceRef.current?.container
        if (!container) return

        const measure = () => {
            const rect = container.getBoundingClientRect()
            setMetrics((prev) =>
                prev && prev.height === rect.height && prev.width === rect.width
                    ? prev
                    : { height: rect.height, width: rect.width },
            )
        }

        measure()
        // Only re-measure here. Updating keen is left to the layout effect keyed
        // on `metrics`, so it runs after React commits the new slide widths —
        // calling update() now would re-measure the still-stale widths.
        const observer = new ResizeObserver(measure)
        observer.observe(container)
        return () => observer.disconnect()
    }, [instanceRef, imageUrls.length, effectiveMaxHeight])

    const heroImageReady =
        imageUrls.length === 0 || !prioritizeHero || loadedImages.has(0)
    const showArrows =
        imageUrls.length > 1 && (!prioritizeHero || heroImageReady)

    return (
        <div
            style={{
                width: '100%',
                backgroundColor: `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`,
                boxSizing: 'border-box',
                position: 'relative',
                zIndex: 0,
            }}
        >
            <div
                style={{
                    position: 'relative',
                    overflow: 'hidden',
                    ...galleryBoxHeight(effectiveMaxHeight, slideRatios[0]),
                }}
            >
                <div
                    ref={sliderRef}
                    className={`keen-slider ${PRODUCT_IMAGE_CAROUSEL_MAIN_KEEN_CLASS}`}
                    style={{ height: '100%' }}
                >
                    {mainSlides}
                </div>

                {!heroImageReady && !heroHasLqip && (
                    <MainGalleryLoadingOverlay
                        role="status"
                        aria-live="polite"
                        aria-label="Product photos loading"
                    >
                        <MainSlideShimmer aria-hidden />
                        <LoadingPhotosLabel>
                            Loading photos&hellip;
                        </LoadingPhotosLabel>
                    </MainGalleryLoadingOverlay>
                )}

                {showArrows && (
                    <>
                        <ArrowButton
                            direction="left"
                            onClick={handlePrev}
                            aria-label="Previous image"
                        >
                            <CarouselChevron width={20} direction="left" />
                        </ArrowButton>
                        <ArrowButton
                            direction="right"
                            onClick={handleNext}
                            aria-label="Next image"
                        >
                            <CarouselChevron width={20} direction="right" />
                        </ArrowButton>
                    </>
                )}
            </div>

            {isFullscreen && (
                <FullscreenGallery
                    imageUrls={imageUrls}
                    initialIndex={currentSlide}
                    onClose={closeFullscreen}
                    onIndexChange={followFullscreenSlide}
                />
            )}
        </div>
    )
}

const ProductImageSlideEmotion = styled.div({
    width: 'auto',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`,
    overflow: 'hidden',
    minHeight: 0,
    height: '100%',
    // The rail is a fixed height, so a squarer image than the box leaves slack.
    // Beside a buy box that starts at the top, centring that slack reads as the
    // gallery sitting lower than the title; anchoring the fitted image to the
    // top of its box lines the two up.
    ...inScrollingDesktop({ img: { objectPosition: 'top' } }),
})

const SlideMediaFrame = styled.div({
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    height: '100%',
    maxHeight: '100%',
})

const mainSlideShimmerKeyframes = keyframes`
    0% {
        background-position: 200% 0;
    }
    100% {
        background-position: -200% 0;
    }
`

const MainGalleryLoadingOverlay = styled.div({
    position: 'absolute',
    inset: 0,
    zIndex: 3,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    pointerEvents: 'none',
    backgroundColor: `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`,
})

const LoadingPhotosLabel = styled.span({
    position: 'relative',
    zIndex: 1,
    fontSize: scaledFontSize(13),
    ...getTypographyRoleStyle('captionText', {
        fontWeight: 400,
        lineHeight: 1.3,
    }),
    color: `color-mix(in srgb, var(${PRIMARY_PAGE_TEXT_COLOR_VAR}) 72%, transparent)`,
    textAlign: 'center',
    padding: '0 16px',
})

const MainSlideShimmer = styled.div({
    position: 'absolute',
    inset: 0,
    background: `linear-gradient(
        90deg,
        color-mix(in srgb, var(${PRIMARY_PAGE_TEXT_COLOR_VAR}) 6%, var(${PRIMARY_PAGE_FILL_COLOR_VAR}) 94%) 0%,
        color-mix(in srgb, var(${PRIMARY_PAGE_TEXT_COLOR_VAR}) 14%, var(${PRIMARY_PAGE_FILL_COLOR_VAR}) 86%) 50%,
        color-mix(in srgb, var(${PRIMARY_PAGE_TEXT_COLOR_VAR}) 6%, var(${PRIMARY_PAGE_FILL_COLOR_VAR}) 94%) 100%
    )`,
    backgroundSize: '200% 100%',
    animation: `${mainSlideShimmerKeyframes} 1.4s ease-in-out infinite`,
})

const ArrowButton = styled.button<{ direction: 'left' | 'right' }>(
    ({ direction }) => ({
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        ...(direction === 'left' ? { left: 8 } : { right: 8 }),
        width: 32,
        height: 32,
        borderRadius: '50%',
        border: 'none',
        background: 'rgba(255, 255, 255, 0.85)',
        color: '#333',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        zIndex: 2,
        boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
        transition: 'background 0.15s ease, transform 0.15s ease',
        '&:hover': {
            background: 'rgba(255, 255, 255, 1)',
            transform: 'translateY(-50%) scale(1.08)',
        },
        '&:active': {
            transform: 'translateY(-50%) scale(0.95)',
        },
    }),
)

const FullscreenSlide = styled.div({
    position: 'relative',
    width: '100vw',
    height: '100vh',
})

const CloseButton = styled.button({
    position: 'absolute',
    top: 24,
    right: 24,
    background: 'rgba(255, 255, 255, 0.1)',
    border: 'none',
    borderRadius: '50%',
    width: 40,
    height: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontSize: scaledFontSize(16),
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    zIndex: 100001,
    '&:hover': {
        background: 'rgba(255, 255, 255, 0.2)',
        transform: 'scale(1.1)',
    },
    '&:active': {
        transform: 'scale(0.95)',
    },
})

const ImageCounter = styled.div({
    position: 'absolute',
    bottom: 24,
    left: '50%',
    transform: 'translateX(-50%)',
    background: 'rgba(0, 0, 0, 0.6)',
    color: 'white',
    padding: '8px 16px',
    borderRadius: '0.2rem',
    fontSize: scaledFontSize(14),
    ...getTypographyRoleStyle('captionText', {
        fontWeight: 500,
    }),
})

const ModalOverlay = styled(InspectOverlayHost)({
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.95)',
    backdropFilter: 'blur(8px)',
    zIndex: 99999,
})

const ModalContent = styled(InspectOverlayHost)({
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 100000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
})

const Portal: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    return createPortal(children, document.body)
}
